/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import React from 'react';
import { LanguageProvider } from './src/hooks/useLanguage';

/**
 * 防止浏览器扩展（如钱包插件）与开发环境冲突
 * Prevent browser extensions (like wallet plugins) from conflicting with development environment
 */
export const onClientEntry = () => {
  if (typeof window !== 'undefined') {
    // 钱包扩展常见的全局属性
    const walletProperties = ['ethereum', 'solana', 'phantom', 'web3', 'tronWeb', 'okxwallet'];
    
    // 创建一个安全的属性设置器
    const createSafePropertySetter = (propName) => {
      let value = window[propName];
      
      // 如果属性已存在，保留其值
      if (propName in window) {
        return;
      }
      
      try {
        Object.defineProperty(window, propName, {
          get() {
            return value;
          },
          set(newValue) {
            // 静默接受设置，但不覆盖现有值
            if (value === undefined) {
              value = newValue;
            }
          },
          configurable: true,
          enumerable: true
        });
      } catch (error) {
        // 如果无法定义属性，静默处理
        // eslint-disable-next-line no-console
        console.warn(`Cannot define property ${propName}:`, error.message);
      }
    };

    // 为常见的钱包属性创建安全设置器
    walletProperties.forEach(createSafePropertySetter);

    // 重写 Object.defineProperty 以处理扩展冲突
    const originalDefineProperty = Object.defineProperty;
    
    Object.defineProperty = function(obj, prop, descriptor) {
      try {
        return originalDefineProperty.call(Object, obj, prop, descriptor);
      } catch (error) {
        // 处理只读属性错误
        if (error.message && (
          error.message.includes('Cannot assign to read only property') ||
          error.message.includes('Cannot redefine property')
        )) {
          console.warn(`Extension conflict detected for property: ${prop}. Ignoring redefinition.`);
          return obj;
        }
        // 其他错误继续抛出
        throw error;
      }
    };

    // 捕获全局错误，防止钱包扩展错误影响页面
    const originalErrorHandler = window.onerror;
    window.onerror = function(message, source) {
      // 检查是否是钱包扩展相关的错误
      if (
        message && (
          message.includes('Cannot assign to read only property') ||
          message.includes('Cannot redefine property') ||
          message.includes('inpage.js') ||
          message.includes('evmAsk.js') ||
          source && (source.includes('extension://') || source.includes('chrome-extension://'))
        )
      ) {
        // eslint-disable-next-line no-console
        console.warn('Wallet extension error suppressed:', message);
        return true; // 阻止错误冒泡
      }
      
      // 调用原始错误处理器
      if (originalErrorHandler) {
        return originalErrorHandler.apply(this, arguments);
      }
      
      return false;
    };

    // 处理未捕获的Promise拒绝
    window.addEventListener('unhandledrejection', function(event) {
      if (
        event.reason && 
        event.reason.message && (
          event.reason.message.includes('Cannot assign to read only property') ||
          event.reason.message.includes('Cannot redefine property') ||
          event.reason.message.includes('extension')
        )
      ) {
        console.warn('Wallet extension promise rejection suppressed:', event.reason);
        event.preventDefault();
      }
    });

    // 在开发环境中添加额外的调试信息
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('Wallet extension conflict protection enabled');
    }
  }
};

export const wrapRootElement = ({ element }) => <LanguageProvider>{element}</LanguageProvider>;
